import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Container, Typography, Grid, Button } from "@mui/material";

import EngineeringIcon from "@mui/icons-material/Engineering";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import HomeWorkIcon from "@mui/icons-material/HomeWork";

import construction from "../../pictures/theme.png";

import ClientStory from "../Client-Corner/ClientStory";
import CountUp from "react-countup";
import OfficeMap from "../OfficeMap";
import { Link as RouterLink } from "react-router-dom";

function Home() {
  const [scrollY, setScrollY] = useState(0);
  const servicesRef = useRef(null);
  const [servicesVisible, setServicesVisible] = useState(false);

  const services = useMemo(() => [
    {
      title: "Construction",
      desc: "Complete residential and commercial construction services.",
      icon: <EngineeringIcon sx={{ fontSize: 46 }} />,
      colors: ["#8c94ff", "#72c9e3", "#ffffff"],
      subtopics: [
        "Residential",
        "Commercial",
        "Renovation",
        "Traditional construction",
        "100% Transparent",
        "Site supervision",
        "Quality Standard Checks",
      ],
    },
    {
      title: "Architecture",
      desc: "Creative architectural designs tailored for your projects.",
      icon: <ArchitectureIcon sx={{ fontSize: 46 }} />,
      colors: ["#8c94ff", "#72c9e3", "#ffffff"],
      subtopics: [
        "House planning",
        "3D elevation",
        "Space optimization",
        "Structural layouts",
        "BOQ & cost planning",
      ],
    },
    {
      title: "Interior Design",
      desc: "Elegant interior solutions that enhance your living spaces.",
      icon: <HomeWorkIcon sx={{ fontSize: 46 }} />,
      colors: ["#8c94ff", "#72c9e3", "#ffffff"],
      subtopics: [
        "Living room Design",
        "Bedroom Interiors",
        "Modular kitchen",
        "Lighting and decor ideas",
        "False ceiling design",
      ],
    },
  ], []);

  const [activeServiceIndex, setActiveServiceIndex] = useState(null);
  const [displayPoints, setDisplayPoints] = useState({});
  // const [hoveredServiceIndex, setHoveredServiceIndex] = useState(null); // eslint-disable-next-line

  const shuffleArray = (arr) => {
    const copied = [...arr];
    for (let i = copied.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copied[i], copied[j]] = [copied[j], copied[i]];
    }
    return copied;
  };

  const handleServiceHover = (service, index) => {
    setActiveServiceIndex(index);
    setDisplayPoints((prev) => ({
      ...prev,
      [index]: shuffleArray(service.subtopics).slice(0, 5),
    }));
  };

  const handleServiceLeave = () => {
    setTimeout(() => {
      setActiveServiceIndex(null);
      // setHoveredServiceIndex(null);
    }, 180);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setServicesVisible(true);
        }
      },
      { threshold: 0.35 },
    );

    if (servicesRef.current) {
      observer.observe(servicesRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
  if (activeServiceIndex === null) return;

  const interval = setInterval(() => {
    const currentService = services[activeServiceIndex];
    setDisplayPoints((prev) => ({
      ...prev,
      [activeServiceIndex]: shuffleArray(currentService.subtopics).slice(0, 5),
    }));
  }, 1200);

  return () => clearInterval(interval);
}, [activeServiceIndex, services]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box>
      <Box
  sx={{
    position: "relative",
    minHeight: { xs: "auto", md: "100vh" },
    py: { xs: 10, sm: 12, md: 0 },
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    color: "#fff",
  }}
>
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      backgroundImage: `url(${construction})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      zIndex: 0,
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(180deg, rgba(75,76,83,0.94) 0%, rgba(15,17,65,0.76) 55%, rgba(8,10,40,0.88) 100%)",
      },
    }}
  />

  <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
    <Grid
      container
      spacing={{ xs: 4, md: 6 }}
      alignItems="center"
      justifyContent="space-between"
    >
      <Grid item xs={12} md={7}>
        <Box
          sx={{
            maxWidth: 700,
            opacity: Math.max(1 - scrollY / 600, 0),
            transform: {
              xs: "none",
              md: `translateY(${scrollY * 0.12}px)`,
            },
            transition: "all 0.15s ease-out",
            willChange: "transform, opacity",
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: "2rem",
                sm: "2.6rem",
                md: "3.4rem",
                lg: "4rem",
              },
              fontWeight: 800,
              lineHeight: 1.15,
              color: "#fff",
              textShadow: "0 8px 24px rgba(0,0,0,0.55)",
              mb: 2,
            }}
          >
            Build Your Dream Home With Trust
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
              color: "rgba(255,255,255,0.88)",
              textShadow: "0 4px 12px rgba(0,0,0,0.45)",
              mb: 4,
              maxWidth: { xs: "100%", md: 560 },
              mx: { xs: "auto", md: 0 },
              animation: "slideInLeft 0.9s ease-out 0.2s both",
              "@keyframes slideInLeft": {
                from: { opacity: 0, transform: "translateX(-30px)" },
                to: { opacity: 1, transform: "translateX(0)" },
              },
            }}
          >
            Traditional & Transparent Construction with clear pricing,
            guided execution, and dependable project delivery.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <Button
            component={RouterLink}
              to="/contact-us"
              variant="contained"
              size="large"
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                px: 4,
                py: 1.6,
                borderRadius: "14px",
                color: "#fff",
                borderColor: "rgba(255,255,255,0.7)",
                background: "#250a7d",
                boxShadow: "0 10px 25px rgba(65, 0, 245, 0.48)",
                fontWeight: 700,
                "&:hover": {
                  background: "#7176ae",
                  transform: "translateY(-2px)",
                  boxShadow: "0 16px 32px rgba(41, 7, 166, 0.77)",
                },
              }}
            >
              Get Free Consultation
            </Button>

            <Button
            component={RouterLink}
              to="/construction-plans"
              variant="outlined"
              size="large"
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                px: 4,
                py: 1.6,
                borderRadius: "14px",
                color: "#fff",
                borderColor: "rgba(255,255,255,0.7)",
                fontWeight: 700,
                "&:hover": {
                  background: "rgba(255,255,255,0.12)",
                  borderColor: "#fff",
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 30px rgba(255,255,255,0.18)",
                },
              }}
            >
              View Construction Plans
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={5}>
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: 420, md: 460 },
            mx: "auto",
            background: "rgba(255,255,255,0.14)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.22)",
            borderRadius: "24px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.34)",
            p: { xs: 3, sm: 4 },
            animation: "slideInRight 1s ease-out 0.4s both",
            "@keyframes slideInRight": {
              from: { opacity: 0, transform: "translateX(40px)" },
              to: { opacity: 1, transform: "translateX(0)" },
            },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", py: 1 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.4rem" },
                    fontWeight: 800,
                    color: "#ffb74d",
                    lineHeight: 1,
                  }}
                >
                  <CountUp end={50} duration={3} />+
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.88)", mt: 1 }}>
                  Projects Completed
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                  borderRadius: "18px",
                  background: "rgba(255,255,255,0.08)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.8rem", sm: "2.1rem" },
                    fontWeight: 800,
                    color: "#7986cb",
                    lineHeight: 1,
                  }}
                >
                  <CountUp end={10} duration={3} />+
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.88)", mt: 1 }}>
                  Years Experience
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                  borderRadius: "18px",
                  background: "rgba(255,255,255,0.08)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.8rem", sm: "2.1rem" },
                    fontWeight: 800,
                    color: "#64b5f6",
                    lineHeight: 1,
                  }}
                >
                  <CountUp end={100} duration={3} />%
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.88)", mt: 1 }}>
                  Transparent Work
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  </Container>
</Box>

      <Box
        sx={{
          background:
            "linear-gradient(180deg, #d6d7d9 50%, #0c065d 100%, #020c28 100%)",
          py: 10,
        }}
      >
        <Container
          ref={servicesRef}
          sx={{
            py: 12,
            position: "relative",
            borderRadius: 4,
            overflow: "hidden",
            background:
              "linear-gradient(rgba(10,10,40,0.9), rgba(10,10,40,0.95))",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 40px)",
              animation: "moveGrid 10s linear infinite",
            },
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight="bold"
            sx={{ mb: 8, color: "#fff", position: "relative", zIndex: 2 }}
          >
            Our Services
          </Typography>

          <Grid
            container
            spacing={6}
            justifyContent="center"
            sx={{ position: "relative", zIndex: 2 }}
          >
            {services.map((item, i) => {
              const isActive = activeServiceIndex === i;
              const points = displayPoints[i] || item.subtopics;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={i}
                  display="flex"
                  justifyContent="center"
                >
                  <Box
                    sx={{
                      position: "relative",
                      perspective: "1400px",
                      width: 320,
                      height: isActive ? 420 : 340,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      overflow: "visible",
                    }}
                    onMouseEnter={() => {
                      // setHoveredServiceIndex(i);
                      handleServiceHover(item, i);
                    }}
                    onMouseLeave={handleServiceLeave}
                    onClick={() => {
                      setActiveServiceIndex((prev) => (prev === i ? null : i));
                      handleServiceHover(item, i);
                    }}
                  >
                    <Box
                      className={
                        servicesVisible ? "serviceCube active" : "serviceCube"
                      }
                      sx={{
                        position: "relative",
                        width: 220,
                        height: 220,
                        transformStyle: "preserve-3d",
                        zIndex: 2,
                      }}
                    >
                      {/* <Box
            className="cubePart cubeBack"
            sx={{
              background: `linear-gradient(145deg, ${item.colors[0]}, ${item.colors[1]})`,
            }}
          /> */}

                      <Box
                        className="cubePart cubeBack"
                        sx={{
                          background: isActive
                            ? "linear-gradient(145deg, #f8faff, #e7ecff)"
                            : `linear-gradient(145deg, ${item.colors[1]}, ${item.colors[2]})`,
                          color: isActive ? "#14095c" : "#fff",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                          padding: "20px",
                          overflow: "hidden",
                          zIndex: 3,
                        }}
                      >
                        {isActive && (
                          <Box
                            sx={{
                              width: "100%",
                              animation: "middleCardFade 0.4s ease",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              sx={{
                                color: "#14095c",
                                mb: 1.2,
                                fontSize: "1rem",
                              }}
                            >
                              {item.title}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.9,
                              }}
                            >
                              {points.map((point, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      background: "#3d44b8",
                                      mt: "7px",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#2d2d2d",
                                      fontSize: "0.82rem",
                                      lineHeight: 1.35,
                                    }}
                                  >
                                    {point}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box
                        className="cubePart cubeFront"
                        sx={{
                          background: isActive
                            ? "linear-gradient(145deg, #dff7ff, #ffffff)"
                            : "linear-gradient(145deg, #86c9dd, #dcd8ff)",
                          color: "#111",
                          border: isActive
                            ? "2px solid rgba(255,255,255,0.8)"
                            : "none",
                        }}
                      >
                        <Box sx={{ color: "#1a075d", mb: 1 }}>{item.icon}</Box>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ mb: 1, color: "#111" }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#444", lineHeight: 1.6, px: 1 }}
                        >
                          {item.desc}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>

        <Container sx={{ py: 12, position: "relative" }}>
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight="bold"
            sx={{ mb: 2 }}
            color="#1a075d"
          >
            Why Choose Us
          </Typography>

          <Typography
            textAlign="center"
            sx={{ color: "#4b4b4b", mb: 8, maxWidth: 720, mx: "auto" }}
          >
            We combine craftsmanship, transparent process, and design-led
            execution to create spaces that feel refined and built to last.
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {[
              {
                title: "10+ Years Experience",
                desc: "Deep construction knowledge with proven delivery across residential and commercial projects.",
                accent: "#5c6fb5",
              },
              {
                title: "Professional Consultation",
                desc: "Expert guidance from concept to completion so your decisions stay confident.",
                accent: "#8d96f7",
              },
               {
                title: "Transparent Pricing",
                desc: "Clear estimates and honest communication keep the process simple and trustworthy.",
                accent: "#737ed2",
              },
              {
                title: "Quality Assurance",
                desc: "Every stage is checked carefully to keep workmanship precise and reliable.",
                accent: "#6468e8",
              },
              {
                title: "Innovative Solutions",
                desc: "Creative planning and modern techniques that improve both performance and visual quality.",
                accent: "#7279f6",
              },
              {
                title: "On-Time Delivery",
                desc: "Structured coordination helps us complete work within committed timelines.",
                accent: "#466dcf",
              },
             
              
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box sx={{ perspective: "1500px", height: "100%" }}>
                  <Box
                    sx={{
                      position: "relative",
                      minHeight: 260,
                      borderRadius: 5,
                      transformStyle: "preserve-3d",
                      transform:
                        i % 2 === 0
                          ? "rotateY(-8deg) rotateX(6deg)"
                          : "rotateY(8deg) rotateX(6deg)",
                      transition: "transform 0.45s ease, box-shadow 0.45s ease",
                      animation: `premiumFloat 6s ease-in-out ${i * 0.15}s infinite`,
                      boxShadow: "0 24px 55px rgba(0,0,0,0.18)",
                      "&:hover": {
                        transform:
                          "rotateY(0deg) rotateX(0deg) translateY(-10px) scale(1.03)",
                        boxShadow: "0 30px 70px rgba(0,0,0,0.26)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: 5,
                        background: `linear-gradient(145deg, ${item.accent}33, rgba(255,255,255,0.02))`,
                        transform: "translateZ(-28px) translateY(14px)",
                        filter: "blur(2px)",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        inset: 8,
                        borderRadius: 5,
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        transform: "translateZ(-10px)",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        inset: 16,
                        borderRadius: 4,
                        background:
                          "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(240,244,255,0.88))",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(26,7,93,0.10)",
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transform: "translateZ(24px)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: `linear-gradient(145deg, ${item.accent}, #1a075d)`,
                          color: "#fff",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.16)",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.9)",
                          }}
                        />
                      </Box>

                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: "#1a075d", mb: 1 }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "#4f4f4f", lineHeight: 1.8 }}
                      >
                        {item.desc}
                      </Typography>

                      <Box
                        sx={{
                          mt: 3,
                          height: 4,
                          borderRadius: 999,
                          background: `linear-gradient(90deg, ${item.accent}, rgba(26,7,93,0.2))`,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Container maxWidth="lg">
          <Typography
            variant="overline"
            textAlign="center"
            sx={{ display: "block", color: "#0f0274", mb: 1 }}
          >
            CLIENT CORNER
          </Typography>

          <Typography
            variant="h4"
            textAlign="center"
            fontWeight="bold"
            color="#1a075d"
          >
            What Our Clients Say
          </Typography>

          <Typography textAlign="center" sx={{ color: "#343333", mb: 5 }}>
            Trusted by clients across Chennai with quality and transparency.
          </Typography>

          <Box sx={{ perspective: "1200px" }}>
            <Box
              sx={{
                transformStyle: "preserve-3d",
                animation: "float3D 6s ease-in-out infinite",
              }}
            >
              <ClientStory />
            </Box>
          </Box>
        </Container>
      </Box>

      <OfficeMap />

      <Box
        sx={{
          background:
            "linear-gradient(135deg, #03031e 0%, #494949 50%, #040431 100%, #494949 50%)",
          color: "#fff",
          py: 8,
          textAlign: "center",
        }}
      >
        <Container>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
            Have a Construction Project?
          </Typography>

          <Typography sx={{ mb: 3 }}>
            Let us help you build your dream project.
          </Typography>

          <Button
          component={RouterLink}
  to="/contact-us"
            variant="contained"
            size="large"
            sx={{
              background: "linear-gradient(135deg, #060249 0%, #00006c 100%)",
              px: 5,
              py: 1.5,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 10px 30px rgba(0, 29, 245, 0.4)",
              },
            }}
          >
            CONTACT US TODAY
          </Button>
        </Container>
      </Box>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes moveGrid {
          from { background-position: 0 0; }
          to { background-position: 40px 40px; }
        }

        @keyframes float3D {
          0% { transform: translateY(0px) rotateX(0deg); }
          50% { transform: translateY(-15px) rotateX(3deg); }
          100% { transform: translateY(0px) rotateX(0deg); }
        }

        @keyframes premiumFloat {
          0%, 100% { transform: translateY(0px) rotateX(6deg); }
          50% { transform: translateY(-12px) rotateX(3deg); }
        }

        @keyframes middleCardFade {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .serviceCube {
          transform-style: preserve-3d;
          transition: transform 0.8s ease;
        }

        .cubePart {
          position: absolute;
          width: 240px;
          height: 240px;
          border-radius: 20px;
          box-shadow:
            0 18px 34px rgba(0, 0, 0, 0.28),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
          backface-visibility: hidden;
          transition: all 1s cubic-bezier(0.12, 1, 0.36, 1);
        }

        .cubeBack {
          transform: translate3d(-28px, 28px, -30px);
        }

        .cubeMiddle {
          transform: translate3d(-8px, 8px, 0px);
          display: flex;
        }

        .cubeFront {
          transform: translate3d(40px, -28px, 40px);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          padding: 22px;
        }

        .serviceCube.active .cubeBack {
          transform: translate3d(-45px, 32px, -40px);
        }

        .serviceCube.active .cubeMiddle {
          transform: translate3d(-4px, 10px, 10px);
        }

        .serviceCube.active .cubeFront {
          transform: translate3d(56px, -40px, 34px);
        }

        .serviceCube:hover {
          transform: rotateY(40deg) rotateX(0deg) translateY(-15px);
        }

        @media (max-width: 900px) {
          .cubePart {
            width: 210px;
            height: 210px;
          }

          .cubeBack {
  transform: translate3d(-28px, 28px, -30px);
  z-index: 1;
}

.cubeMiddle {
  transform: translate3d(-8px, 8px, 0px);
  display: flex;
  z-index: 2;
}

.cubeFront {
  transform: translate3d(50px, 22px, 100px);

}

.serviceCube.active .cubeMiddle {
  transform: translate3d(26px, -10px, -100px);
}
          .serviceCube.active .cubeFront {
            transform: translate3d(60px, -45px, 54px);
          }
        }
          
      `}</style>
    </Box>
  );
}

export default Home;
